// src/components/layout/ModuleHeader.tsx
//
// Per-page header — sits below the top-bar. Per design system §6.10, this is
// where the workspace logo (the tenant's mark) lives. The header background
// is the per-app product accent (ARM blue), giving users a peripheral cue
// about which app they're in.

import { useSnapshot } from 'valtio';
import { soaStore } from '../../stores/soaStore';

interface ModuleHeaderProps {
  title: string;
  subtitle?: string;
  /** Optional custom gradient override. Defaults to the product accent gradient. */
  gradient?: string;
  children?: React.ReactNode;
}

export function ModuleHeader({ title, subtitle, gradient, children }: ModuleHeaderProps) {
  const soa = useSnapshot(soaStore);
  const workspaceLogo = soa.companyInfo?.logo;

  // Default gradient uses the product accent (ARM blue). Callers can still
  // pass their own gradient for special pages if needed.
  const background = gradient ?? 'linear-gradient(135deg, var(--color-product), var(--color-product-deep))';

  return (
    <div
      className="sticky top-0 z-10 text-white shadow-lg print:hidden"
      style={{ background }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: workspace logo card (if uploaded) + title */}
          <div className="flex items-center gap-3 min-w-0">
            {workspaceLogo && (
              <div
                className="flex-shrink-0 inline-flex items-center justify-center"
                style={{
                  background: 'white',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 10px',
                  height: '44px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                }}
              >
                <img
                  src={workspaceLogo}
                  alt=""
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    maxHeight: '28px',
                    maxWidth: '120px',
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight truncate m-0">{title}</h1>
              {subtitle && (
                <p className="text-white/80 text-sm truncate">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Right: caller-provided actions (buttons, search etc.) */}
          {children && <div className="flex items-center gap-2 flex-shrink-0">{children}</div>}
        </div>
      </div>
    </div>
  );
}
